import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericFullscreenDialog } from '@/common'
import {
  ImporterFormData,
  validateImporterForm,
} from '../components/ImporterForm.schema'
import { useCallback, useMemo, useState } from 'react'
import {
  useCreateImporter,
  ValidationErrorResponse,
} from '@/features/importers/hooks/importers'
import toast from 'react-hot-toast'
import ImporterForm from '../components/ImporterForm'

export default function ImporterCreateDialog({
  isOpen,
  close,
  organizationId,
}: {
  isOpen: boolean
  close: () => void
  organizationId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: createImporter, isPending: isSaving } =
    useCreateImporter(organizationId)

  const [formData, setFormData] = useState<ImporterFormData>({
    name: '',
    placeOfRegistration: '',
    identificationNumber: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    subDivision: '',
    country: '',
    poBox: '',
  })

  const [fieldErrors, setFieldErrors] = useState<{
    customId?: string
    identificationNumber?: string
  }>({})

  const isSaveDisabled = useMemo(() => {
    const validation = validateImporterForm(formData)
    return isSaving || !validation.success
  }, [isSaving, formData])

  const onClose = useCallback(() => {
    setFormData({
      name: '',
      customId: '',
      placeOfRegistration: '',
      identificationNumber: '',
      street: '',
      streetNumber: '',
      street2: '',
      postCode: '',
      city: '',
      subDivision: '',
      country: '',
      poBox: '',
    })
    setFieldErrors({})
    close()
  }, [close])

  const onSave = useCallback(async () => {
    try {
      await createImporter({
        ...formData,
        customId: formData.customId || undefined,
      })
      toast.success(
        t(
          'importers:createImporterDialog.notifications.importerCreatedSuccessfully'
        )
      )
      onClose()
    } catch (error: any) {
      console.error(error)
      const errorData = error?.response?.data as
        | ValidationErrorResponse
        | undefined

      if (errorData?.errors && Array.isArray(errorData.errors)) {
        errorData.errors.forEach((err) => {
          switch (err.message) {
            case 'CUSTOM_ID_ALREADY_EXISTS':
              setFieldErrors((prev) => ({
                ...prev,
                customId: t('importers:errors.customIdAlreadyExists'),
              }))
              break
            case 'IDENTIFICATION_NUMBER_ALREADY_EXISTS':
              setFieldErrors((prev) => ({
                ...prev,
                identificationNumber: t(
                  'importers:errors.identificationNumberAlreadyExists'
                ),
              }))
              break
          }
        })
      } else {
        toast.error(
          t(
            'importers:createImporterDialog.notifications.failedToCreateImporter'
          )
        )
      }
    }
  }, [createImporter, formData, onClose, t])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('importers:createImporterDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('importers:createImporterDialog.actions.add')}
          </Button>
        </div>
      }
    >
      <ImporterForm
        data={formData}
        onChange={setFormData}
        fieldErrors={fieldErrors}
        resetErrorField={(field) => {
          setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
        }}
      />
    </GenericFullscreenDialog>
  )
}
